import React, { useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  computeAcademicWeeklyStats,
  legacyWeeklyFallbackRate,
} from '../../lib/academicAttendance';

// Phase 72.D / Phase 5 — Academic Weekly Attendance trendline (last 8 weeks).
// All calculation now flows through `lib/academicAttendance.js` — the single
// frontend source of truth that mirrors the backend service exactly.
// "Late - Justified" therefore counts identically to "Late" everywhere.

const computeWeek = (rec) => {
  const days = rec.days || [];
  if (days.length > 0) {
    const s = computeAcademicWeeklyStats(days);
    // Trendline cares only about (rate, attended, missed, noClass, counts).
    return {
      rate: s.attendance_rate,
      attended: s.attended_count,
      missed: s.missed_count,
      noClass: s.no_class_count,
      counts: s.breakdown,
    };
  }
  // Legacy: only the weekly summary status is stored.
  const r = legacyWeeklyFallbackRate(rec.status);
  if (r === null) return { rate: 0, attended: 0, missed: 0, noClass: 1, counts: {} };
  if (r === 0) return { rate: 0, attended: 0, missed: 1, noClass: 0, counts: {} };
  return { rate: 100, attended: 1, missed: 0, noClass: 0, counts: {} };
};

const shortLabel = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
};

const TrendBadge = ({ delta }) => {
  if (delta === null) return null;
  if (delta > 2) return <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300"><TrendingUp className="w-3 h-3" />+{delta}%</span>;
  if (delta < -2) return <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-300"><TrendingDown className="w-3 h-3" />{delta}%</span>;
  return <span className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400"><Minus className="w-3 h-3" />{delta >= 0 ? '+' : ''}{delta}%</span>;
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 text-[11px] shadow-sm" data-testid="trend-tooltip">
      <div className="font-medium">Week of {p.label}</div>
      <div className="text-emerald-700 dark:text-emerald-300 font-semibold">Rate: {p.rate}%</div>
      <div className="text-gray-600 dark:text-gray-300 mt-0.5">
        Attended: {p.attended} · Missed: {p.missed}
        {p.noClass > 0 ? ` · No Class: ${p.noClass}` : ''}
      </div>
    </div>
  );
};

const AcademicAttendanceTrendline = ({ attendance, compact = false }) => {
  const series = useMemo(() => {
    const sorted = [...(attendance || [])].sort((a, b) => {
      return String(a.week_start_date || '').localeCompare(String(b.week_start_date || ''));
    });
    const last8 = sorted.slice(-8);
    return last8.map(rec => {
      const c = computeWeek(rec);
      return {
        label: shortLabel(rec.week_start_date),
        week_start_date: rec.week_start_date,
        rate: c.rate,
        attended: c.attended,
        missed: c.missed,
        noClass: c.noClass,
      };
    });
  }, [attendance]);

  const total = series.length;
  if (total === 0) {
    return (
      <div className="border border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center" data-testid="trend-empty">
        <p className="text-xs text-gray-500 dark:text-gray-400">Not enough attendance history yet.</p>
      </div>
    );
  }
  const latest = series[total - 1];
  const previous = total >= 2 ? series[total - 2] : null;
  const delta = previous ? Math.round(latest.rate - previous.rate) : null;
  const avg8 = total > 0 ? Math.round(series.reduce((a, s) => a + s.rate, 0) / total) : 0;

  return (
    <div className={`border border-gray-200 dark:border-gray-700 rounded-lg ${compact ? 'p-2' : 'p-3'} bg-white dark:bg-gray-800`} data-testid="academic-attendance-trendline">
      <div className="flex justify-between items-baseline gap-2 mb-2">
        <div>
          <p className={`${compact ? 'text-[11px]' : 'text-xs'} font-medium text-gray-700 dark:text-gray-200`}>
            {compact ? 'Academic Attendance Trend' : `Attendance Trend — Last ${total} Week${total === 1 ? '' : 's'}`}
          </p>
          <p className="text-[10px] text-gray-500 dark:text-gray-400">{compact ? `Avg ${avg8}% · ${total} week${total === 1 ? '' : 's'}` : `Avg over window: ${avg8}%`}</p>
        </div>
        <div className="text-right">
          <p className={`${compact ? 'text-base' : 'text-lg'} font-semibold text-emerald-700 dark:text-emerald-300`} data-testid="trend-latest-rate">{latest.rate}%</p>
          <p className="text-[10px]"><TrendBadge delta={delta} /></p>
        </div>
      </div>

      {total < 2 ? (
        <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-gray-500 dark:text-gray-400 text-center ${compact ? 'py-1' : 'py-3'}`} data-testid="trend-not-enough">
          {compact ? 'Not enough history.' : 'Not enough attendance history yet. Add another week to see the trendline.'}
        </p>
      ) : (
        <div style={{ width: '100%', height: compact ? 60 : 110 }}>
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              {!compact && <XAxis dataKey="label" stroke="#9ca3af" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />}
              {!compact && <YAxis domain={[0, 100]} stroke="#9ca3af" tick={{ fontSize: 10 }} width={28} axisLine={false} tickLine={false} />}
              {!compact && <ReferenceLine y={80} stroke="#d1d5db" strokeDasharray="3 3" />}
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="rate" stroke="#059669" strokeWidth={2} dot={{ r: compact ? 2 : 3, fill: '#059669' }} activeDot={{ r: compact ? 3 : 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default AcademicAttendanceTrendline;
